import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { edgeFunctionErrorMessage } from "@/lib/edgeFunctionError";
import { signatureSendOutcome } from "@/lib/signatureSend";

export interface BudgetSignature {
  id: string;
  budget_id: string;
  signer_name: string;
  signer_email: string;
  order_index: number;
  token: string;
  status: "pending" | "signed" | "rejected";
  signature_type: "drawn" | "typed" | "certificate" | null;
  signature_data: string | null;
  certificate_info: Record<string, unknown> | null;
  rejection_reason: string | null;
  sent_at: string | null;
  signed_at: string | null;
  rejected_at: string | null;
  ip_address: string | null;
  user_agent: string | null;
  viewed_at: string | null;
  viewed_ip: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export function useBudgetSignatures(budgetId: string | undefined) {
  return useQuery({
    queryKey: ["budget-signatures", budgetId],
    queryFn: async () => {
      if (!budgetId) return [];
      const { data, error } = await supabase
        .from("budget_signatures" as never)
        .select("*")
        .eq("budget_id", budgetId)
        .order("order_index", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as BudgetSignature[];
    },
    enabled: !!budgetId,
  });
}

export interface SendSignatureRequest {
  budgetId: string;
  flow: "parallel" | "sequential";
  signers: { name: string; email: string }[];
}

export function useSendBudgetSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SendSignatureRequest) => {
      const { data, error } = await supabase.functions.invoke("send-budget-signature", {
        body: payload,
      });
      if (error)
        throw new Error(
          await edgeFunctionErrorMessage(error, "Erro ao enviar solicitação de assinatura"),
        );
      return data;
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["budget-signatures", vars.budgetId] });
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      const outcome = signatureSendOutcome(data);
      if (outcome.ok) toast.success(outcome.message);
      else toast.warning(outcome.message);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
